"use client";

import { useEffect } from "react";

/**
 * Landing InitiateCheckout — dual setup. Fires the browser Pixel +
 * server-side Conversions API `InitiateCheckout` with the SAME `eventId`
 * (Meta deduplicates) whenever a visitor clicks a link to a Polar hosted
 * checkout (`buy.polar.sh`). The pricing cards are CMS/server-rendered, so
 * rather than thread an `onClick` through every card variant, we delegate a
 * single `document` click listener — it catches every checkout link
 * site-wide (pricing cards, content cards, section CTAs, nav).
 *
 * Anonymous, no PII — same data category as the landing PageView CAPI
 * (`/api/conversions/track` only sends `_fbp`/`_fbc`/IP/UA, gated on the
 * `fulton_ad_optout` cookie). If `window.fbq` is absent the Pixel didn't
 * render (opted out / no id) — we skip BOTH the browser event and the server
 * call, keeping them in lock-step with consent. Mirrors
 * `FacebookPixelPageView`.
 */

const CHECKOUT_HOST = "buy.polar.sh";

function isCheckoutLink(href: string): boolean {
  try {
    return new URL(href).hostname === CHECKOUT_HOST;
  } catch {
    return false;
  }
}

export function FacebookPixelCheckoutClicks() {
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      const anchor = target?.closest?.("a[href]") as HTMLAnchorElement | null;
      if (!anchor || !isCheckoutLink(anchor.href)) return;

      const fbq = (window as unknown as { fbq?: (...a: unknown[]) => void }).fbq;
      if (!fbq) return; // Pixel not active (opted out / not configured).

      const eventId =
        typeof crypto !== "undefined" && "randomUUID" in crypto
          ? crypto.randomUUID()
          : `ic-${Date.now()}-${Math.round(Math.random() * 1e9)}`;

      fbq("track", "InitiateCheckout", {}, { eventID: eventId });

      try {
        void fetch("/api/conversions/track", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          // Survive the navigation to Polar (target=_self or _blank).
          keepalive: true,
          body: JSON.stringify({
            eventName: "InitiateCheckout",
            eventId,
            eventSourceUrl: window.location.href,
          }),
        });
      } catch {
        /* ignore */
      }
    };

    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);

  return null;
}

export default FacebookPixelCheckoutClicks;
